import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from './PublicLayout';

// Custom hook for scroll animations
const useInView = () => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.15 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return [ref, isInView];
};

const About = () => {
  const [mounted, setMounted] = useState(false);
  const [storyRef, storyInView] = useInView();
  const [valuesRef, valuesInView] = useInView();
  const [principlesRef, principlesInView] = useInView();
  const [transparencyRef, transparencyInView] = useInView();

  useEffect(() => {
    setMounted(true);
  }, []);

  const values = [
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
        </svg>
      ),
      title: 'AI for Everyone',
      desc: 'We believe anyone should be able to build AI features — not just programmers or prompt engineers.',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
        </svg>
      ),
      title: 'Simple to Build, Easy to Deploy',
      desc: 'Create prompts in the UI, then integrate via SDK. No complicated setup for either workflow.',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: 'No Hidden Costs',
      desc: 'We don\'t mark up AI costs. You pay your AI provider directly at their published rates.',
    },
    {
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5V6.75a4.5 4.5 0 119 0v3.75M3.75 21.75h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H3.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
        </svg>
      ),
      title: 'Your Data, Your Control',
      desc: 'Your prompts and data belong to you. Export everything anytime. No lock-in, no restrictions.',
    },
  ];

  const principles = [
    {
      number: '01',
      title: 'Empower Your Ideas',
      desc: 'You know what you need best. We give you tools to bring your AI ideas to life, no coding required.',
    },
    {
      number: '02',
      title: 'Never Lose Your Work',
      desc: 'Every version is saved automatically. Made a mistake? Go back to what worked with one click.',
    },
    {
      number: '03',
      title: 'Transparent Pricing',
      desc: 'AI costs can add up. We show you exactly what you spend so you stay in control.',
    },
    {
      number: '04',
      title: 'Respect Your Privacy',
      desc: 'We don\'t train on your data. Your prompts stay private, and you can export everything anytime.',
    },
  ];

  const transparencyItems = [
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
        </svg>
      ),
      title: 'Your Data Stays Yours',
      desc: 'We never train on your prompts. Your intellectual property remains confidential and is never shared with third parties.',
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
        </svg>
      ),
      title: 'Encrypted at Rest',
      desc: 'API keys and sensitive data are encrypted using industry-standard encryption. Your credentials are protected.',
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
        </svg>
      ),
      title: 'No Hidden Fees',
      desc: 'ForgeVault does not mark up API costs. You pay your LLM provider directly at their published rates.',
    },
    {
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
        </svg>
      ),
      title: 'Full Export Anytime',
      desc: 'Export your prompts to any format at any time. No data hostage situations, no export fees, no restrictions.',
    },
  ];

  return (
    <PublicLayout>
      <div className="px-6 lg:px-12 pt-8 pb-12 lg:pt-12 lg:pb-16">
        <div className="max-w-5xl mx-auto">
          
          {/* Hero Section */}
          <div 
            className={`text-center mb-20 transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            style={{ transitionDelay: '0.1s' }}
          >
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <img 
                src="/logo.png"
                alt="ForgeVault Logo" 
                className="w-20 h-20 lg:w-24 lg:h-24 object-contain"
              />
            </div>

            <h1 className="text-3xl lg:text-5xl font-bold mb-6 leading-tight text-text-primary">
              About ForgeVault
            </h1>
            <p className="text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed text-palegreen-70">
              We're building a tool that lets anyone create powerful AI features — no coding required.
              Whether you run a business, create content, or just want AI to work your way.
            </p>
          </div>

          {/* The Story Section */}
          <div 
            ref={storyRef}
            className="mb-24"
          >
            <div 
              className={`p-8 lg:p-12 rounded-2xl bg-gradient-forest-subtle border border-success-10 transition-all duration-700 ease-out ${storyInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-success-15 text-primary-500">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                  </svg>
                </div>
                <h2 className="text-xl lg:text-2xl font-bold text-text-primary">
                  The Problem We Saw
                </h2>
              </div>
              
              <div className="space-y-5 text-base lg:text-lg leading-relaxed text-palegreen-75">
                <p>
                  Building AI features requires collaboration. Product teams understand user needs, while 
                  developers handle integration. But prompt management often falls through the cracks.
                </p>
                <p>
                  ForgeVault bridges this gap. Non-technical users create and test prompts in plain English. 
                  Developers integrate them via a production-ready SDK. Everyone works in the same platform.
                </p>
                <p>
                  Our goal: make AI accessible to everyone — from business owners creating their first prompt 
                  to developers deploying at scale with version control and monitoring.
                </p>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div 
            ref={valuesRef}
            className="mb-24"
          >
            <h2 
              className={`text-2xl lg:text-3xl font-bold mb-4 text-center text-text-primary transition-all duration-500 ease-out ${valuesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
            >
              What Drives Us
            </h2>
            <p 
              className={`text-center mb-10 max-w-2xl mx-auto text-palegreen-60 transition-all duration-500 ease-out ${valuesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
              style={{ transitionDelay: '0.1s' }}
            >
              The principles that guide every decision we make
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {values.map((value, idx) => (
                <div
                  key={idx}
                  className={`p-6 rounded-xl bg-bg-dark-accent border border-success-10 transition-all duration-500 ease-out ${valuesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                  style={{ transitionDelay: `${0.2 + idx * 0.1}s` }}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 bg-success-10 text-primary-500">
                    {value.icon}
                  </div>
                  <h3 className="font-semibold text-lg mb-2 text-text-primary">
                    {value.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-palegreen-60">
                    {value.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Design Principles */}
          <div 
            ref={principlesRef}
            className="mb-24"
          >
            <h2 
              className={`text-2xl lg:text-3xl font-bold mb-4 text-center text-text-primary transition-all duration-500 ease-out ${principlesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
            >
              Our Philosophy
            </h2>
            <p 
              className={`text-center mb-10 max-w-2xl mx-auto text-palegreen-60 transition-all duration-500 ease-out ${principlesInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
              style={{ transitionDelay: '0.1s' }}
            >
              The beliefs that shaped ForgeVault
            </p>
            
            <div className="space-y-4">
              {principles.map((principle, idx) => (
                <div
                  key={idx}
                  className={`flex gap-5 p-5 rounded-xl bg-bg-dark-accent border border-success-10 transition-all duration-500 ease-out ${principlesInView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-5'}`}
                  style={{ transitionDelay: `${0.15 + idx * 0.08}s` }}
                >
                  <span className="text-2xl font-bold flex-shrink-0 text-primary-500/30 font-mono">
                    {principle.number}
                  </span>
                  <div>
                    <h3 className="font-semibold text-base mb-1 text-text-primary">
                      {principle.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-palegreen-60">
                      {principle.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Trust & Transparency */}
          <div 
            ref={transparencyRef}
            className="mb-20"
          >
            <h2 
              className={`text-2xl lg:text-3xl font-bold mb-4 text-center text-text-primary transition-all duration-500 ease-out ${transparencyInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
            >
              Our Commitments
            </h2>
            <p 
              className={`text-center mb-10 max-w-2xl mx-auto text-palegreen-60 transition-all duration-500 ease-out ${transparencyInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
              style={{ transitionDelay: '0.1s' }}
            >
              What you can always expect from ForgeVault
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {transparencyItems.map((item, idx) => (
                <div
                  key={idx}
                  className={`flex gap-4 p-5 rounded-xl bg-bg-dark-accent border border-success-10 transition-all duration-500 ease-out ${transparencyInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                  style={{ transitionDelay: `${0.2 + idx * 0.1}s` }}
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-success-10 text-primary-500">
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm mb-1 text-text-primary">
                      {item.title}
                    </h3>
                    <p className="text-xs leading-relaxed text-palegreen-60">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div 
            className={`text-center transition-all duration-700 ease-out ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '1s' }}
          >
            <div className="p-8 lg:p-10 rounded-2xl bg-gradient-forest-subtle border border-success-15">
              <h2 className="text-xl lg:text-2xl font-bold mb-3 text-text-primary">
                Ready to build your first AI feature?
              </h2>
              <p className="text-base mb-6 max-w-xl mx-auto text-palegreen-60">
                Start free, no credit card needed. Build something amazing in minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/signup"
                  className="px-6 py-3 text-sm font-semibold rounded-lg transition-all duration-300 inline-flex items-center justify-center gap-2 bg-gradient-cta text-black shadow-glow hover:-translate-y-0.5 hover:shadow-glow-lg"
                >
                  Start Free Now
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </Link>
                <Link
                  to="/features"
                  className="px-6 py-3 text-sm font-semibold rounded-lg transition-all duration-300 inline-flex items-center justify-center gap-2 bg-transparent text-palegreen-80 border border-success-20 hover:bg-success-10 hover:border-success-50"
                >
                  See What You Can Build
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default About;
